import * as Sentry from '@sentry/react'

// Global state to track Sentry initialization status
let isSentryInitialized = false

// Helper function to check if Sentry is available and properly initialized
export const isSentryAvailable = (): boolean => {
  return isSentryInitialized && typeof Sentry !== 'undefined'
}

// Rate limiting for error reporting
const errorRateLimiter = new Map<string, { count: number; firstSeen: number }>()
const MAX_SAME_ERROR_COUNT = 5
const RATE_LIMIT_WINDOW = 60000 // 1 minute

// Comprehensive transport error patterns
const TRANSPORT_ERROR_PATTERNS = [
  // Network errors
  'Failed to fetch',
  'ERR_BLOCKED_BY_CLIENT',
  'ERR_NETWORK',
  'ERR_INTERNET_DISCONNECTED',
  'ERR_CONNECTION_REFUSED',
  'ERR_CONNECTION_RESET',
  'ERR_CONNECTION_ABORTED',
  'ERR_TIMED_OUT',
  'ERR_NAME_NOT_RESOLVED',
  'ERR_PROXY_CONNECTION_FAILED',

  // Sentry-specific transport errors
  'ingest.sentry.io',
  'sentry_client',
  'sentry.io',
  'dsn.ingest.sentry.io',

  // Ad blocker patterns
  'uBlock',
  'AdBlock',
  'Privacy Badger',
  'Ghostery',
  'DuckDuckGo',
  'Disconnect',

  // CORS and security errors
  'CORS',
  'Cross-Origin Request Blocked',
  'Access to fetch',
  'Not allowed by Access-Control-Allow-Origin',

  // Browser security errors
  'SecurityError',
  'The request is not allowed',
  'Blocked a frame',
  'Content Security Policy',
  'CSP',

  // Generic fetch/xhr failures
  'NetworkError when attempting to fetch resource',
  'Load failed',
  'The Internet connection appears to be offline',
  'A network error occurred',
  'Request failed',
  'XHR error',
]

// Helper function to check if error is transport-related
const isTransportError = (event: {
  exception?: { values?: Array<{ value?: string; type?: string }> }
  request?: { url?: string }
}): boolean => {
  const errorMessage = event.exception?.values?.[0]?.value || ''
  const errorType = event.exception?.values?.[0]?.type || ''
  const requestUrl = event.request?.url || ''

  // Check error message patterns
  const hasTransportPattern = TRANSPORT_ERROR_PATTERNS.some(
    pattern =>
      errorMessage.toLowerCase().includes(pattern.toLowerCase()) ||
      requestUrl.toLowerCase().includes(pattern.toLowerCase())
  )

  // Check for specific error types
  const isNetworkErrorType =
    errorType === 'TypeError' &&
    (errorMessage.includes('fetch') ||
      errorMessage.includes('network') ||
      errorMessage.includes('connection'))

  // Check request URL for Sentry domains
  const isSentryRequest = requestUrl.includes('sentry.io') || requestUrl.includes('ingest.sentry')

  return hasTransportPattern || isNetworkErrorType || isSentryRequest
}

// Helper function for rate limiting
const shouldRateLimit = (errorKey: string): boolean => {
  const now = Date.now()
  const errorInfo = errorRateLimiter.get(errorKey)

  if (!errorInfo) {
    errorRateLimiter.set(errorKey, { count: 1, firstSeen: now })
    return false
  }

  // Reset counter if window has passed
  if (now - errorInfo.firstSeen > RATE_LIMIT_WINDOW) {
    errorRateLimiter.set(errorKey, { count: 1, firstSeen: now })
    return false
  }

  // Increment counter
  errorInfo.count++

  // Rate limit if too many of the same error
  return errorInfo.count > MAX_SAME_ERROR_COUNT
}

// Export helper functions for external use (after function definitions)
export const isTransportErrorCheck = isTransportError
export const getSentryTransportErrorPatterns = (): string[] => [...TRANSPORT_ERROR_PATTERNS]

// Clear rate limiter (useful for testing or manual reset)
export const clearSentryRateLimit = (): void => {
  errorRateLimiter.clear()
  if (isSentryAvailable()) {
    console.info('Sentry rate limiter cleared')
  }
}

// ============================================================================
// Feature Detection Functions
// ============================================================================

// Browser compatibility detection for Sentry
export const isBrowserCompatible = (): boolean => {
  try {
    // Check for essential browser features required by Sentry
    const hasPromise = typeof Promise !== 'undefined'
    const hasFetch = typeof fetch !== 'undefined'
    const hasLocalStorage = (() => {
      try {
        const test = '__sentry_test__'
        localStorage.setItem(test, test)
        localStorage.removeItem(test)
        return true
      } catch {
        return false
      }
    })()
    const hasJSON = typeof JSON !== 'undefined' && typeof JSON.stringify === 'function'
    const hasConsole = typeof console !== 'undefined'
    const hasDocument = typeof document !== 'undefined'
    const hasWindow = typeof window !== 'undefined'

    return (
      hasPromise && hasFetch && hasLocalStorage && hasJSON && hasConsole && hasDocument && hasWindow
    )
  } catch {
    return false
  }
}

// Network connectivity check for Sentry endpoints
export const checkSentryConnectivity = async (): Promise<{
  isConnected: boolean
  latency?: number
  error?: string
}> => {
  try {
    const dsn = import.meta.env.VITE_SENTRY_DSN
    if (!dsn) {
      return { isConnected: false, error: 'No DSN configured' }
    }

    // Extract Sentry API endpoint from DSN
    const dsnUrl = new URL(dsn)
    const apiUrl = `${dsnUrl.protocol}//${dsnUrl.host}/api/0/projects/`

    const startTime = performance.now()

    // Perform a lightweight connectivity check
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 5000) // 5 second timeout

    try {
      await fetch(apiUrl, {
        method: 'HEAD',
        mode: 'no-cors', // Avoid CORS issues
        signal: controller.signal,
        cache: 'no-cache',
      })

      clearTimeout(timeout)
      const latency = performance.now() - startTime

      return {
        isConnected: true,
        latency: Math.round(latency),
      }
    } catch (fetchError) {
      clearTimeout(timeout)

      // If it's an abort error, it means timeout
      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        return { isConnected: false, error: 'Connection timeout' }
      }

      // For no-cors mode, opaque responses still indicate connectivity
      return { isConnected: true, latency: performance.now() - startTime }
    }
  } catch (error) {
    return {
      isConnected: false,
      error: error instanceof Error ? error.message : 'Unknown connectivity error',
    }
  }
}

// Detailed Sentry SDK initialization status
export const getSentryInitializationStatus = (): {
  isInitialized: boolean
  hasHub: boolean
  hasClient: boolean
  hasTransport: boolean
  integrations: string[]
  error?: string
} => {
  try {
    if (!isSentryInitialized || typeof Sentry === 'undefined') {
      return {
        isInitialized: false,
        hasHub: false,
        hasClient: false,
        hasTransport: false,
        integrations: [],
        error: 'Sentry not initialized',
      }
    }

    // Check Client availability directly (newer Sentry API)
    const client = (Sentry as { getClient?: () => unknown }).getClient?.()
    const hasClient = !!client
    const hasHub = hasClient // Hub exists if client exists

    // Check Transport availability (if client exists)
    let hasTransport = false
    if (client && typeof client === 'object' && client !== null && 'getTransport' in client) {
      try {
        const transport = (client as { getTransport?: () => unknown }).getTransport?.()
        hasTransport = !!transport
      } catch {
        hasTransport = false
      }
    }

    // Get active integrations
    const integrations: string[] = []
    if (
      client &&
      typeof client === 'object' &&
      client !== null &&
      'getIntegrationByName' in client
    ) {
      try {
        // Common Sentry integrations to check
        const commonIntegrations = [
          'BrowserTracing',
          'Replay',
          'GlobalHandlers',
          'TryCatch',
          'Breadcrumbs',
          'LinkedErrors',
          'HttpContext',
          'Dedupe',
        ]

        for (const integrationName of commonIntegrations) {
          try {
            const integration = (
              client as { getIntegrationByName?: (name: string) => unknown }
            ).getIntegrationByName?.(integrationName)
            if (integration) {
              integrations.push(integrationName)
            }
          } catch {
            // Integration not available, skip
          }
        }
      } catch {
        // Error getting integrations
      }
    }

    return {
      isInitialized: true,
      hasHub,
      hasClient,
      hasTransport,
      integrations,
    }
  } catch (error) {
    return {
      isInitialized: false,
      hasHub: false,
      hasClient: false,
      hasTransport: false,
      integrations: [],
      error: error instanceof Error ? error.message : 'Unknown initialization error',
    }
  }
}

// Feature-specific availability checks
export const getSentryFeatureAvailability = (): {
  canCaptureException: boolean
  canCaptureMessage: boolean
  canAddBreadcrumb: boolean
  canSetUser: boolean
  canSetTags: boolean
  canSetContext: boolean
  canStartTransaction: boolean
  hasSessionReplay: boolean
  hasPerformanceMonitoring: boolean
} => {
  const baseAvailable = isSentryAvailable()

  if (!baseAvailable) {
    return {
      canCaptureException: false,
      canCaptureMessage: false,
      canAddBreadcrumb: false,
      canSetUser: false,
      canSetTags: false,
      canSetContext: false,
      canStartTransaction: false,
      hasSessionReplay: false,
      hasPerformanceMonitoring: false,
    }
  }

  try {
    const status = getSentryInitializationStatus()

    return {
      canCaptureException: typeof Sentry.captureException === 'function',
      canCaptureMessage: typeof Sentry.captureMessage === 'function',
      canAddBreadcrumb: typeof Sentry.addBreadcrumb === 'function',
      canSetUser: typeof Sentry.setUser === 'function',
      canSetTags: typeof Sentry.setTags === 'function',
      canSetContext: typeof Sentry.setContext === 'function',
      canStartTransaction: false, // startTransaction is deprecated in newer Sentry versions
      hasSessionReplay: status.integrations.includes('Replay'),
      hasPerformanceMonitoring: status.integrations.includes('BrowserTracing'),
    }
  } catch {
    return {
      canCaptureException: false,
      canCaptureMessage: false,
      canAddBreadcrumb: false,
      canSetUser: false,
      canSetTags: false,
      canSetContext: false,
      canStartTransaction: false,
      hasSessionReplay: false,
      hasPerformanceMonitoring: false,
    }
  }
}

// Comprehensive health check function
export const performSentryHealthCheck = async (): Promise<{
  isHealthy: boolean
  browserCompatible: boolean
  networkConnected: boolean
  initialization: ReturnType<typeof getSentryInitializationStatus>
  features: ReturnType<typeof getSentryFeatureAvailability>
  connectivity?: Awaited<ReturnType<typeof checkSentryConnectivity>>
  recommendations: string[]
}> => {
  const browserCompatible = isBrowserCompatible()
  const initialization = getSentryInitializationStatus()
  const features = getSentryFeatureAvailability()

  // Only check connectivity if basic setup is working
  let connectivity: Awaited<ReturnType<typeof checkSentryConnectivity>> | undefined
  let networkConnected = false

  if (browserCompatible && initialization.isInitialized) {
    try {
      connectivity = await checkSentryConnectivity()
      networkConnected = connectivity.isConnected
    } catch {
      networkConnected = false
    }
  }

  // Generate recommendations
  const recommendations: string[] = []

  if (!browserCompatible) {
    recommendations.push('Browser does not support required features for Sentry')
  }

  if (!initialization.isInitialized) {
    recommendations.push('Sentry SDK is not properly initialized')
  } else {
    if (!initialization.hasHub) recommendations.push('Sentry Hub not available')
    if (!initialization.hasClient) recommendations.push('Sentry Client not available')
    if (!initialization.hasTransport) recommendations.push('Sentry Transport not available')
  }

  if (!networkConnected && connectivity) {
    recommendations.push(`Network connectivity issue: ${connectivity.error || 'Unknown error'}`)
  }

  if (!features.canCaptureException) {
    recommendations.push('Cannot capture exceptions - core Sentry function unavailable')
  }

  const isHealthy =
    browserCompatible &&
    initialization.isInitialized &&
    initialization.hasClient &&
    features.canCaptureException &&
    networkConnected

  return {
    isHealthy,
    browserCompatible,
    networkConnected,
    initialization,
    features,
    connectivity,
    recommendations,
  }
}

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development'
  const isEnabled = import.meta.env.VITE_SENTRY_ENABLE === 'true'

  // Only initialize Sentry if DSN is provided and enabled
  if (!dsn || !isEnabled) {
    console.log('Sentry is disabled or DSN not provided')
    return
  }

  // Store original console methods outside try block for proper scope
  const originalConsoleError = console.error
  const originalConsoleWarn = console.warn
  const originalConsoleLog = console.log

  try {
    // Create temporary suppression for Sentry initialization only
    const suppressSentryInitLogs = () => {
      console.error = (...args: unknown[]) => {
        if (isSentryInternalLog(...args)) {
          // Silently ignore only Sentry-specific transport logs
          return
        }
        originalConsoleError.apply(console, args as Parameters<typeof originalConsoleError>)
      }

      console.warn = (...args: unknown[]) => {
        if (isSentryInternalLog(...args)) {
          return
        }
        originalConsoleWarn.apply(console, args as Parameters<typeof originalConsoleWarn>)
      }

      console.log = (...args: unknown[]) => {
        // For logs we keep the older patterns (Logger [log] + outcome messages)
        const logStrings: string[] = []
        for (const arg of args) {
          if (typeof arg === 'string') logStrings.push(arg)
        }
        const joined = logStrings.join(' ')

        if (
          joined.includes('Sentry Logger [log]') ||
          joined.includes('Recording outcome') ||
          joined.includes('Flushing outcomes') ||
          joined.includes('Sending outcomes')
        ) {
          return
        }
        originalConsoleLog.apply(console, args as Parameters<typeof originalConsoleLog>)
      }
    }

    // Apply temporary console suppression only during initialization
    suppressSentryInitLogs()

    // Initialize Sentry
    Sentry.init({
      dsn,
      environment,
      integrations: [
        // Browser tracing integration
        Sentry.browserTracingIntegration(),
        // Session replay integration
        Sentry.replayIntegration({
          // Capture 10% of all sessions,
          // plus 100% of sessions with an error
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: environment === 'development' ? 1.0 : 0.1,
      // Session Replay
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      // Set additional options based on environment
      debug: false, // Always disable debug logging to prevent transport error spam
      // Suppress internal Sentry logging in all environments
      _experiments: {
        captureFailedRequests: false,
      },
      // Enhanced initial context
      initialScope: {
        tags: {
          component: 'react-frontend',
          version: '1.0.0',
        },
        contexts: {
          app: {
            name: 'AI Hack Frontend',
            version: '1.0.0',
          },
        },
      },
      beforeSend(event, hint) {
        try {
          // Enhanced error filtering and context
          if (environment === 'development') {
            console.log('Sentry Event:', event, hint)
          }

          const errorMessage = event.exception?.values?.[0]?.value || ''
          const errorType = event.exception?.values?.[0]?.type || ''

          // 1. Filter out transport errors (comprehensive check)
          if (isTransportError(event)) {
            const logLevel = environment === 'development' ? 'warn' : 'debug'
            console[logLevel]('Sentry: Filtered transport error:', {
              message: errorMessage,
              type: errorType,
              url: event.request?.url,
              environment,
            })
            return null
          }

          // 2. Rate limiting for repeated errors
          const errorKey = `${errorType}:${errorMessage.substring(0, 100)}`
          if (shouldRateLimit(errorKey)) {
            if (environment === 'development') {
              console.warn('Sentry: Rate limited error:', errorKey)
            }
            return null
          }

          // 3. Filter out known non-actionable errors
          const nonActionablePatterns = [
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
            'Script error.',
            'Non-Error promise rejection captured',
            'ChunkLoadError',
            'Loading chunk',
            'Loading CSS chunk',
            'window.webkit.messageHandlers', // iOS Safari specific
            "Can't find variable: webkit", // iOS Safari specific
            '__gCrWeb', // iOS Chrome specific
            '_cordovaNative', // Cordova specific
            'conditionerAMD', // AdBlock specific
            'isSecureContext', // Browser compatibility
            'webkitURL is not defined', // Browser compatibility
          ]

          if (
            nonActionablePatterns.some(
              pattern => errorMessage.includes(pattern) || event.message?.includes(pattern)
            )
          ) {
            if (environment === 'development') {
              console.debug('Sentry: Filtered non-actionable error:', errorMessage)
            }
            return null
          }

          // 4. Filter out browser extension errors
          const extensionPatterns = [
            'extension://',
            'chrome-extension://',
            'moz-extension://',
            'safari-extension://',
            'ms-browser-extension://',
            'webkit-masked-url://',
          ]

          const stackTrace = event.exception?.values?.[0]?.stacktrace?.frames || []
          const hasExtensionFrame = stackTrace.some(frame =>
            extensionPatterns.some(
              pattern => frame.filename?.includes(pattern) || frame.abs_path?.includes(pattern)
            )
          )

          if (hasExtensionFrame) {
            if (environment === 'development') {
              console.debug('Sentry: Filtered browser extension error:', errorMessage)
            }
            return null
          }

          // 5. Enhanced context for actionable errors
          if (event.exception) {
            // Add useful debugging context
            event.contexts = {
              ...event.contexts,
              runtime: {
                userAgent: navigator.userAgent,
                url: window.location.href,
                timestamp: new Date().toISOString(),
              },
            }
          }

          return event
        } catch (beforeSendError) {
          // If beforeSend itself fails, log but don't break error reporting
          console.warn('Sentry beforeSend error:', beforeSendError)
          return event
        }
      },
      beforeBreadcrumb(breadcrumb) {
        // Filter out noisy breadcrumbs
        if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
          return null
        }

        // Enhance navigation breadcrumbs
        if (breadcrumb.category === 'navigation') {
          breadcrumb.data = {
            ...breadcrumb.data,
            timestamp: new Date().toISOString(),
          }
        }

        return breadcrumb
      },
    })

    // Set initialization flag to true on successful init
    isSentryInitialized = true
    console.log(`Sentry initialized successfully for ${environment} environment`)
  } catch (error) {
    // Silent failure - log warning but don't break the application
    console.warn('Sentry initialization failed, continuing without error reporting:', error)
    isSentryInitialized = false
  } finally {
    // CRITICAL: Always restore original console methods, regardless of success or failure
    // This ensures application errors are never permanently suppressed
    console.error = originalConsoleError
    console.warn = originalConsoleWarn
    console.log = originalConsoleLog
  }
}

// ユーザーコンテキスト設定関数
export const setSentryUser = (user: {
  id: string
  email: string
  fullName: string
  spaceId?: string
}) => {
  if (!isSentryAvailable()) {
    console.warn('Sentry not available, skipping user context setting')
    return
  }

  try {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.fullName,
    })

    // 追加のユーザーコンテキスト
    Sentry.setContext('user_details', {
      fullName: user.fullName,
      spaceId: user.spaceId,
      loginTime: new Date().toISOString(),
    })

    // ユーザー関連タグ
    Sentry.setTag('user_id', user.id)
    Sentry.setTag('user_authenticated', 'true')

    if (user.spaceId) {
      Sentry.setTag('space_id', user.spaceId)
    }
  } catch (error) {
    console.warn('Failed to set Sentry user context:', error)
  }
}

// ユーザーログアウト時のクリア
export const clearSentryUser = () => {
  if (!isSentryAvailable()) {
    console.warn('Sentry not available, skipping user context clearing')
    return
  }

  try {
    Sentry.setUser(null)
    Sentry.setTag('user_authenticated', 'false')
    Sentry.setTag('user_id', '')
    Sentry.setTag('space_id', '')
  } catch (error) {
    console.warn('Failed to clear Sentry user context:', error)
  }
}

// カスタムタグ設定
export const setSentryTags = (tags: Record<string, string>) => {
  if (!isSentryAvailable()) {
    console.warn('Sentry not available, skipping tag setting')
    return
  }

  try {
    Object.entries(tags).forEach(([key, value]) => {
      Sentry.setTag(key, value)
    })
  } catch (error) {
    console.warn('Failed to set Sentry tags:', error)
  }
}

// カスタムコンテキスト設定
export const setSentryContext = (key: string, context: Record<string, unknown>) => {
  if (!isSentryAvailable()) {
    console.warn('Sentry not available, skipping context setting')
    return
  }

  try {
    Sentry.setContext(key, context)
  } catch (error) {
    console.warn('Failed to set Sentry context:', error)
  }
}

// 構造化ログ関数
export const sentryLogger = {
  // 情報レベル（ブレッドクラム）
  info: (message: string, extra?: Record<string, unknown>) => {
    if (!isSentryAvailable()) {
      console.info(`[Sentry Fallback] ${message}`, extra)
      return
    }

    try {
      Sentry.addBreadcrumb({
        message,
        level: 'info',
        category: 'app',
        data: extra,
        timestamp: Date.now() / 1000,
      })
    } catch (error) {
      console.warn('Failed to add Sentry breadcrumb:', error)
      console.info(`[Sentry Fallback] ${message}`, extra)
    }
  },

  // 警告レベル
  warn: (message: string, extra?: Record<string, unknown>) => {
    if (!isSentryAvailable()) {
      console.warn(`[Sentry Fallback] ${message}`, extra)
      return
    }

    try {
      Sentry.addBreadcrumb({
        message,
        level: 'warning',
        category: 'app',
        data: extra,
        timestamp: Date.now() / 1000,
      })

      // 重要な警告はSentryに送信
      if (extra?.important) {
        Sentry.captureMessage(message, 'warning')
      }
    } catch (error) {
      console.warn('Failed to log warning to Sentry:', error)
      console.warn(`[Sentry Fallback] ${message}`, extra)
    }
  },

  // エラーレベル
  error: (message: string, error?: Error, extra?: Record<string, unknown>) => {
    if (!isSentryAvailable()) {
      console.error(`[Sentry Fallback] ${message}`, error, extra)
      return
    }

    try {
      // ブレッドクラム追加
      Sentry.addBreadcrumb({
        message,
        level: 'error',
        category: 'app',
        data: extra,
        timestamp: Date.now() / 1000,
      })

      // エラーコンテキスト設定
      if (extra) {
        Sentry.setContext('error_context', extra)
      }

      // エラー送信
      if (error) {
        Sentry.captureException(error)
      } else {
        Sentry.captureMessage(message, 'error')
      }
    } catch (sentryError) {
      console.warn('Failed to log error to Sentry:', sentryError)
      console.error(`[Sentry Fallback] ${message}`, error, extra)
    }
  },

  // API エラー専用
  apiError: (
    endpoint: string,
    method: string,
    status: number,
    error: Error | string,
    extra?: Record<string, unknown>
  ) => {
    const errorMessage = typeof error === 'string' ? error : error.message

    if (!isSentryAvailable()) {
      console.error(`[Sentry Fallback] API Error: ${method} ${endpoint} - ${errorMessage}`, {
        endpoint,
        method,
        status,
        error,
        timestamp: new Date().toISOString(),
        ...extra,
      })
      return
    }

    try {
      Sentry.setContext('api_error', {
        endpoint,
        method,
        status,
        timestamp: new Date().toISOString(),
        ...extra,
      })

      Sentry.setTag('api_endpoint', endpoint)
      Sentry.setTag('api_method', method)
      Sentry.setTag('api_status', status.toString())

      if (typeof error === 'object') {
        Sentry.captureException(error)
      } else {
        Sentry.captureMessage(`API Error: ${method} ${endpoint} - ${errorMessage}`, 'error')
      }
    } catch (sentryError) {
      console.warn('Failed to log API error to Sentry:', sentryError)
      console.error(`[Sentry Fallback] API Error: ${method} ${endpoint} - ${errorMessage}`, {
        endpoint,
        method,
        status,
        error,
        ...extra,
      })
    }
  },

  // ユーザーアクション記録
  userAction: (action: string, details?: Record<string, unknown>) => {
    if (!isSentryAvailable()) {
      console.info(`[Sentry Fallback] User Action: ${action}`, details)
      return
    }

    try {
      Sentry.addBreadcrumb({
        message: `User Action: ${action}`,
        level: 'info',
        category: 'user',
        data: {
          action,
          timestamp: new Date().toISOString(),
          ...details,
        },
      })
    } catch (error) {
      console.warn('Failed to log user action to Sentry:', error)
      console.info(`[Sentry Fallback] User Action: ${action}`, details)
    }
  },

  // パフォーマンス記録
  performance: (operation: string, duration: number, details?: Record<string, unknown>) => {
    if (!isSentryAvailable()) {
      if (duration > 1000) {
        console.warn(
          `[Sentry Fallback] Slow operation detected: ${operation} took ${duration}ms`,
          details
        )
      } else {
        console.info(`[Sentry Fallback] Performance: ${operation} (${duration}ms)`, details)
      }
      return
    }

    try {
      Sentry.addBreadcrumb({
        message: `Performance: ${operation}`,
        level: 'info',
        category: 'performance',
        data: {
          operation,
          duration,
          unit: 'ms',
          ...details,
        },
      })

      // 遅い操作は警告として記録
      if (duration > 1000) {
        Sentry.captureMessage(`Slow operation detected: ${operation} took ${duration}ms`, 'warning')
      }
    } catch (error) {
      console.warn('Failed to log performance to Sentry:', error)
      if (duration > 1000) {
        console.warn(
          `[Sentry Fallback] Slow operation detected: ${operation} took ${duration}ms`,
          details
        )
      } else {
        console.info(`[Sentry Fallback] Performance: ${operation} (${duration}ms)`, details)
      }
    }
  },
}

// NOTE: SentryErrorFallback has been moved to src/components/common/sentry/SentryErrorFallback.tsx
// to support React.lazy loading and component separation

/* --------------------------------------------------------------------------
 * Utility helpers
 * -------------------------------------------------------------------------- */

/**
 * Returns TRUE when the provided console arguments correspond to an internal
 * Sentry transport / logger message that should be hidden from the end user.
 *
 * The previous implementation concatenated `args.join(' ')`, which coerced
 * non-string values (objects, arrays, functions …) to the string
 * "[object Object]".  This prevented correct pattern matching and occasionally
 * filtered legitimate application errors.  We now inspect each argument
 * individually **without mutating its original representation** so that:
 *   • Only string values (or Error objects ➜ their `message`) are considered
 *     for pattern matching.
 *   • Non-string arguments are ignored for suppression purposes but still
 *     forwarded to the original console method when required.
 */
export function isSentryInternalLog(...args: unknown[]): boolean {
  const stringFragments: string[] = []

  for (const arg of args) {
    if (typeof arg === 'string') {
      stringFragments.push(arg)
    } else if (arg instanceof Error) {
      stringFragments.push(arg.message)
    }
  }

  // Quick exit if nothing to evaluate
  if (stringFragments.length === 0) return false

  const message = stringFragments.join(' ')

  // Base patterns – keep in sync with existing logic
  if (
    message.includes('Sentry Logger [error]') ||
    message.includes('Error while sending envelope') ||
    message.includes('Encountered error running transport request') ||
    // More specific filtering for Sentry transport errors
    (message.includes('POST https://') &&
      message.includes('.sentry.io') &&
      (message.includes('Failed to fetch') || message.includes('ERR_BLOCKED_BY_CLIENT'))) ||
    message.includes('net::ERR_BLOCKED_BY_CLIENT')
  ) {
    return true
  }

  return false
}
